import { Box, Link, Stack } from '@mui/material';
import { Button } from 'system/atoms';
import { FieldWrapper } from 'common/form';
import {
  CAMPAIGN_CSV_TEMPLATE_URL,
  TWILIO_PHONE_NUMBER_FORMAT_URL
} from 'core/constants/urls';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import { AlertCard } from 'components/AlertCard';
import { useDropzone } from 'react-dropzone';
import { useCallback } from 'react';

type CampaignCreationCSVFileUploadProps = {
  fileName: string | null;
  numRows: number;
  handleSkip: (() => void) | null;
  onUpload: (file: File) => void;
};

export const CampaignCreationCSVFileUpload: React.FC<
  CampaignCreationCSVFileUploadProps
> = ({ fileName, numRows, handleSkip, onUpload }) => {
  const { track } = useTracking();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      onUpload(file);
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'text/csv': []
    },
    maxFiles: 1
  });
  return (
    <FieldWrapper
      label="Find a .csv file"
      subLabel={
        <>
          <Box>
            <span>
              To prevent errors, use{' '}
              <Link
                href={TWILIO_PHONE_NUMBER_FORMAT_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  track(TrackingEventName.CampaignCsvUploadE164formatUrlClicked)
                }
              >
                E.164 numbering format
              </Link>{' '}
              for phone numbers.
            </span>
          </Box>
          <span>
            Add column headers to identify phone, email, first name, last name,
            or{' '}
            <Link
              href={CAMPAIGN_CSV_TEMPLATE_URL}
              target="_blank"
              onClick={() =>
                track(
                  TrackingEventName.CampaignCsvUploadContactsTemplateDownloaded
                )
              }
            >
              download this template
            </Link>
          </span>
        </>
      }
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: 2,
          width: '100%'
        }}
      >
        {fileName && (
          <AlertCard
            title={`${fileName} uploaded`}
            subtitle={
              <Box>
                {numRows ? (
                  <Box display="flex">
                    Contains {numRows} candidate(s).{' '}
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      <Link href="javascript:void(0)">
                        Upload a different file
                      </Link>
                    </div>
                  </Box>
                ) : (
                  <span>
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      <Link href="javascript:void(0)">
                        Upload a different file
                      </Link>
                    </div>
                  </span>
                )}
              </Box>
            }
            type="success"
            width="100%"
          />
        )}

        {!fileName && (
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              <Button variant="primary">Upload</Button>
            </div>
            {handleSkip && (
              <Button
                variant="secondary"
                onClick={() => {
                  handleSkip();
                }}
              >
                Skip
              </Button>
            )}
          </Stack>
        )}
      </Box>
    </FieldWrapper>
  );
};
